import { type ReactNode } from 'react'
import { Typography, Box } from '@mui/material'
import { HelpOutline as DefaultIcon } from '@mui/icons-material'
import { EntityModal } from './EntityModal'
import { themeColors } from '../../app/layout/theme'

interface ConfirmDialogProps {
  /** Controls dialog visibility */
  open: boolean
  /** Called when the user cancels or clicks outside */
  onClose: () => void
  /** Called when the user clicks the confirm button */
  onConfirm: () => void
  /** Dialog title */
  title?: string
  /** Body text. Can be a plain string or rich JSX */
  description?: ReactNode
  /** Extra content rendered below the description (e.g. an Alert) */
  children?: ReactNode
  /** Label for the confirm/action button. Defaults to "Confirm" */
  confirmLabel?: string
  /** Label for the cancel button. Defaults to "Cancel" */
  cancelLabel?: string
  /**
   * MUI button color for the confirm button.
   * Use 'error' for destructive actions, 'primary' (default) for safe ones.
   */
  confirmColor?: 'primary' | 'error' | 'warning' | 'success' | 'info'
  /** Optional icon shown in the dialog header */
  icon?: ReactNode
  /** Shows a loading spinner on the confirm button and disables both buttons */
  isConfirming?: boolean
  /** Disable the confirm button without showing a spinner */
  confirmDisabled?: boolean
}

/**
 * A flexible, reusable confirmation dialog.
 *
 * @example
 * // Discard warning
 * <ConfirmDialog
 *   open={open}
 *   title="Discard changes?"
 *   description="All unsaved changes will be lost."
 *   confirmLabel="Yes, discard"
 *   confirmColor="error"
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleDiscard}
 * />
 *
 * @example
 * // Generic action
 * <ConfirmDialog
 *   open={open}
 *   title="Submit report?"
 *   description="This will notify all reviewers."
 *   confirmLabel="Submit"
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleSubmit}
 *   isConfirming={submitting}
 * />
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  icon,
  isConfirming = false,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={onConfirm}
      title={title}
      submitLabel={confirmLabel}
      isSubmitting={isConfirming}
      submitDisabled={confirmDisabled}
      maxWidth="xs"
      icon={icon ?? <DefaultIcon sx={{ fontSize: 18 }} />}
      submitProps={{
        color: confirmColor,
        ...(confirmColor === 'error' && {
          sx: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            background: themeColors.error,
            boxShadow: 'none',
            '&:hover': { background: themeColors.errorLight, color: themeColors.error, boxShadow: 'none' },
          },
        }),
      }}
      actions={
        // Custom actions slot so we can pass a custom cancelLabel
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', width: '100%' }}>
          <Box
            component="button"
            onClick={onClose}
            disabled={isConfirming}
            sx={{
              cursor: isConfirming ? 'not-allowed' : 'pointer',
              border: `1px solid ${themeColors.border}`,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              py: '7px',
              bgcolor: themeColors.surface,
              color: themeColors.text.primary,
              '&:hover:not(:disabled)': { bgcolor: themeColors.surfaceHover },
              '&:disabled': { opacity: 0.5 },
              transition: 'background 0.15s',
            }}
          >
            {cancelLabel}
          </Box>

          <Box
            component="button"
            onClick={onConfirm}
            disabled={isConfirming || confirmDisabled}
            sx={{
              cursor: isConfirming || confirmDisabled ? 'not-allowed' : 'pointer',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '14px',
              px: 3,
              py: '7px',
              color: themeColors.text.inverse,
              bgcolor: confirmColor === 'error' ? themeColors.error
                     : confirmColor === 'warning' ? themeColors.warning
                     : confirmColor === 'success' ? themeColors.success
                     : themeColors.primary,
              '&:hover:not(:disabled)': {
                opacity: 0.88,
              },
              '&:disabled': { opacity: 0.5 },
              transition: 'opacity 0.15s',
            }}
          >
            {isConfirming ? '…' : confirmLabel}
          </Box>
        </Box>
      }
    >
      <Box>
        {description && (
          <Typography sx={{ fontSize: '14px', color: themeColors.text.secondary, lineHeight: 1.6 }}>
            {description}
          </Typography>
        )}
        {children}
      </Box>
    </EntityModal>
  )
}
